import clsx from "clsx";
import { FC } from "react";
import { useTranslation } from "react-i18next";

import { SVG_MUI } from "@ryde/assets";
import {
  NO_VALUE_DISPLAY,
  onActivateKeyDown,
  TOOLTIP_DELAY_BUTTONS,
  useDrawers,
  useHasPermissions,
} from "@ryde/shared";
import { rydeColors } from "@ryde/theme";

import { CheckboxMui } from "../../../../controls";
import { CellSimple } from "../../../../grid";
import { AreaTooltip } from "../../../area-tooltip";
import { DragIconReplacement } from "../../../misc";
import { RideStationInOutIcon } from "../../../ride-controls";
import { FramerTooltip } from "../../../tooltips";
import {
  RideStationDefaultSupplierLookupPin,
  RideStationDefaultSupplierPin,
} from "./ride-station-default-supplier-pin";
import { RideStationDurationTooltip } from "./ride-station-duration-tooltip";
import { RideStationRowBtns } from "./ride-station-row-btns";
import { RideStationStopIndicator } from "./ride-station-stop-indicator";
import { RideStationTimeCell } from "./ride-station-time-cell";
import { RideStationWarningContent } from "./ride-station-warning.content";
import {
  RideStationAreaCellInnerStyled,
  RideStationRowStyled,
  RouteWaypointRowBadge,
  StationBoxCenterStyled,
  StationBoxLeftStyled,
} from "./ride-stations.styled";
import { useRideStationRow } from "./use-ride-station-row";

export const RideStationRow: FC = () => {
  const { t } = useTranslation();
  const { hasPermissions: updateAreaPermission } = useHasPermissions({
    entity: "area",
    action: "update",
  });
  const { hasPermissions: updateStationPermission } = useHasPermissions({
    entity: "station",
    action: "update",
  });

  const {
    index,
    isMobile,
    isGreyed,
    isCollapsed,
    toggleCollapsed,
    stationName,
    stationFullName,
    stationPassengersSeats,
    allPassengersSelected,
    checkStationPassengers,
    duration,
    areaTitle,
    areaGroupName,
    showDefaultSupplierPin,
    greyDefaultAreaMarkerForSkipWithoutPriorityId,
    isSupplierLookupStation,
    areaId,
    stationId,
    rideId,
    rideDto,
    statusType,
    station,
    isStationDraggable,
    attributes,
    listeners,
    isWaypoint,
    waypointVisual,
    realStationOrdinal,
  } = useRideStationRow();
  const { openEditFormDrawer } = useDrawers();

  const handleAreaClick = (): void => {
    if (updateAreaPermission && areaId) {
      openEditFormDrawer({
        entity: "area",
        id: areaId,
      });
    }
  };

  const handleStationClick = (): void => {
    if (updateStationPermission && stationId) {
      openEditFormDrawer({
        entity: "station",
        id: stationId,
      });
    }
  };

  return (
    <RideStationRowStyled>
      {!isMobile && isStationDraggable && (
        <div className="column_grip" {...attributes} {...listeners}>
          <SVG_MUI.GripVertical3SVG className="pointer" />
        </div>
      )}

      {!isMobile && !isStationDraggable && (
        <div className="column_grip">
          <DragIconReplacement />
        </div>
      )}
      <div className="column_checkbox">
        {!isWaypoint && !isGreyed && (
          <CheckboxMui
            width={15}
            height={15}
            checked={allPassengersSelected}
            onCheckboxToggle={checkStationPassengers}
          />
        )}
      </div>
      {!isMobile && (
        <div className="column_passengers">
          {!isWaypoint && (
            <>
              <RideStationInOutIcon
                statusType={statusType}
                isGreyed={isGreyed}
              />
              {Boolean(stationPassengersSeats) && (
                <span>{stationPassengersSeats}</span>
              )}
              {!stationPassengersSeats && (
                <span>
                  <FramerTooltip
                    content={t("ride.NO_PASSENGERS_AT_THIS_STATION")}
                  >
                    {NO_VALUE_DISPLAY}
                  </FramerTooltip>
                </span>
              )}
              {isCollapsed && (
                <SVG_MUI.ChevronDown3SVG
                  className="pointer"
                  onClick={toggleCollapsed}
                />
              )}
              {!isCollapsed && (
                <SVG_MUI.ChevronUp3SVG
                  className="pointer"
                  onClick={toggleCollapsed}
                />
              )}
            </>
          )}
        </div>
      )}
      <div className="column_station_info">
        {isWaypoint ? (
          <StationBoxLeftStyled>
            <RouteWaypointRowBadge
              title={t(waypointVisual.tooltipKey)}
              $category={station?.waypointCategory}
            >
              W
            </RouteWaypointRowBadge>
          </StationBoxLeftStyled>
        ) : (
          <FramerTooltip
            content={`${stationName} ID:${stationId}`}
            mouseEnterDelay={TOOLTIP_DELAY_BUTTONS}
          >
            <StationBoxLeftStyled
              className={clsx({
                pointer: updateStationPermission,
              })}
              onClick={handleStationClick}
            >
              {realStationOrdinal ?? index + 1}
            </StationBoxLeftStyled>
          </FramerTooltip>
        )}
        <StationBoxCenterStyled>
          <CellSimple
            color={isWaypoint ? rydeColors.midGrey : rydeColors.black}
            classNamesAdded={isWaypoint ? "waypoint-name" : ""}
          >
            {isWaypoint
              ? t("stationRoute.waypoint.ROW_LABEL", { address: stationName })
              : stationFullName}
          </CellSimple>
        </StationBoxCenterStyled>
        <RideStationTimeCell />
      </div>
      {!isMobile && (
        <div
          className={clsx({
            column_area: true,
            pointer: updateAreaPermission,
          })}
          onClick={handleAreaClick}
          onKeyDown={onActivateKeyDown(handleAreaClick)}
          role="button"
          tabIndex={0}
        >
          <RideStationAreaCellInnerStyled>
            {showDefaultSupplierPin && (
              <span className="area-default-supplier-tags">
                {isSupplierLookupStation ? (
                  <RideStationDefaultSupplierLookupPin
                    greyDefaultAreaMarkerForSkipWithoutPriorityId={
                      greyDefaultAreaMarkerForSkipWithoutPriorityId
                    }
                  />
                ) : (
                  <RideStationDefaultSupplierPin
                    greyDefaultAreaMarkerForSkipWithoutPriorityId={
                      greyDefaultAreaMarkerForSkipWithoutPriorityId
                    }
                  />
                )}
              </span>
            )}
            <span className="area-title-tooltip-wrap">
              <FramerTooltip
                tooltipDisabled={!areaId}
                mouseEnterDelay={TOOLTIP_DELAY_BUTTONS}
                content={
                  <AreaTooltip
                    areaId={areaId}
                    areaTitle={areaTitle}
                    areaGroupName={areaGroupName}
                    rideId={rideId}
                  />
                }
              >
                <span className="truncate-text area-title-truncate-inner">
                  {areaTitle}
                </span>
              </FramerTooltip>
            </span>
          </RideStationAreaCellInnerStyled>
        </div>
      )}
      {!isMobile && (
        <div className="column_stop">
          <RideStationStopIndicator station={station} />
        </div>
      )}
      <div className="column_duration">
        <div className="column_duration_text">
          <RideStationDurationTooltip rideDto={rideDto} stationIndex={index}>
            {duration}
          </RideStationDurationTooltip>
        </div>
        <RideStationWarningContent station={station} />
      </div>
      {!isMobile && (
        <div className="column_icons">
          <RideStationRowBtns />
        </div>
      )}
    </RideStationRowStyled>
  );
};
